import React, { useCallback, type MouseEvent } from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { CustomThemeButton } from '@atlaskit/button';
import { Box, xcss } from '@atlaskit/primitives';

import { token } from '@atlaskit/tokens';

import { useIntl } from '@atlassian/jira-intl';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	fireUIAnalytics,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import messages from './messages.tsx';

export type Props = {
	onRetry: (e: MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => void;
};

export const Error = ({ onRetry }: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const onRetryClick = useCallback(
		(e: MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'retryViewWorkflowButton');
			// @ts-expect-error - TS2554 - Expected 2 arguments, but got 1.
			onRetry(e);
		},
		[onRetry],
	);

	return (
		<ContextualAnalyticsData sourceName="viewWorkflowError" sourceType={SCREEN}>
			<Wrapper>
				<Box xcss={errorMessageWrapperStyles}>{formatMessage(messages.errorMessage)}</Box>
				<ButtonWrapper>
					<CustomThemeButton
						appearance="link"
						spacing="none"
						onClick={onRetryClick}
						theme={(currentTheme, theme) => {
							const buttonTheme = currentTheme(theme);
							return {
								...buttonTheme,
								buttonStyles: {
									...buttonTheme.buttonStyles,
									alignItems: 'center',
								},
							};
						}}
						interactionName="auto-generated.jira-issue-field-status.common.ui.view-workflow.error.button"
					>
						{formatMessage(messages.tryAgain)}
					</CustomThemeButton>
				</ButtonWrapper>
				<FireScreenAnalytics />
			</Wrapper>
		</ContextualAnalyticsData>
	);
};

export default Error;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	height: '48px',

	borderTop: `${token('border.width.selected')} solid ${token('color.border')}`,
	display: 'flex',
	alignItems: 'center',
	paddingTop: 0,
	paddingRight: token('space.150'),
	paddingBottom: 0,
	paddingLeft: token('space.150'),
	justifyContent: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonWrapper = styled.div({
	display: 'flex',
	flexGrow: 1,
	height: '100%',
});

const errorMessageWrapperStyles = xcss({
	marginRight: 'space.100',
});
